import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, ApiClientError } from '../lib/api';
import { Badge, Button } from '../atoms';
import { Modal } from './Modal';
import { useToast } from './Toast';

/**
 * CalculateFeesModal — idempotently compute an appeal's fee from its resolved
 * contingency/flat/hybrid terms and stage it as a DRAFT invoice (backlog 04.1).
 *
 * POST /api/workflow/appeals/:appealId/calculate-fees. Re-running is safe: the
 * backend recomputes the same line item + draft invoice in place (never a second
 * invoice), so the control can be used to preview/refresh the fee as the appeal's
 * settled value changes. On success it links straight to the staged draft invoice.
 */

interface CalculateFeesResult {
  staged: boolean;
  appeal_id: string;
  invoice_id?: string;
  invoice_number?: string;
  invoice_item_id?: string;
  amount?: number;
  invoice_amount?: number;
  billing_type?: string;
  fee_rate_source?: string;
  reason?: string;
}

const SOURCE_LABEL: Record<string, string> = {
  contract: 'Client contract',
  state: 'State default',
  consultant: 'Consultant rate',
  org_default: 'Firm default',
  default: 'Firm floor rate',
};

const REASON_LABEL: Record<string, string> = {
  nothing_owed: 'No fee is owed on this appeal yet (a contingency fee needs a value reduction).',
  no_reduction_no_terms: 'No fee terms resolve and there is no reduction, so nothing is owed yet.',
};

export interface CalculateFeesModalProps {
  appealId: string;
  open: boolean;
  onClose: () => void;
}

export function CalculateFeesModal({ appealId, open, onClose }: CalculateFeesModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [result, setResult] = useState<CalculateFeesResult | null>(null);

  const mutation = useMutation<CalculateFeesResult, Error, void>({
    mutationFn: () => api.post<CalculateFeesResult>(`/api/workflow/appeals/${appealId}/calculate-fees`, {}),
    onSuccess: (data) => {
      setResult(data);
      queryClient.invalidateQueries({ queryKey: ['appeal', appealId] });
      queryClient.invalidateQueries({ queryKey: ['appeal-activity', appealId] });
      queryClient.invalidateQueries({ queryKey: ['invoices-list'] });
      if (data.staged) {
        toast({ variant: 'success', message: `Fees staged on draft invoice ${data.invoice_number ?? ''}.` });
      } else {
        toast({ variant: 'info', message: 'No fee is owed on this appeal yet.' });
      }
    },
    onError: (err) => {
      const msg = err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
        ? String((err.body as { error: unknown }).error)
        : err.message;
      toast({ variant: 'error', message: msg });
    },
  });

  function handleClose() {
    if (mutation.isPending) return;
    setResult(null);
    onClose();
  }

  const labelStyle: React.CSSProperties = {
    fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)',
  };
  const valueStyle: React.CSSProperties = {
    fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)',
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Calculate fees"
      size="sm"
      footer={
        result ? (
          <>
            <Button variant="secondary" onClick={handleClose}>Close</Button>
            {result.staged && result.invoice_id && (
              <Button variant="primary" onClick={() => { const id = result.invoice_id!; handleClose(); navigate(`/invoices/${id}`); }}>
                View draft invoice
              </Button>
            )}
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={handleClose} disabled={mutation.isPending}>Cancel</Button>
            <Button variant="primary" loading={mutation.isPending} onClick={() => mutation.mutate()}>
              Compute &amp; stage draft
            </Button>
          </>
        )
      }
    >
      {!result ? (
        <p style={{ margin: 0, color: 'var(--color-ink-3)', fontSize: 'var(--fs-md)' }}>
          Computes this appeal's fee from the resolved billing terms (client contract → state
          default → consultant rate → firm default) and stages it as a <strong>draft invoice</strong>.
          Running it again recomputes the same draft in place — it never creates a second invoice.
        </p>
      ) : !result.staged ? (
        <p style={{ margin: 0, color: 'var(--color-ink-3)', fontSize: 'var(--fs-md)' }}>
          {REASON_LABEL[result.reason ?? ''] ?? 'No fee is owed on this appeal yet.'}
        </p>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--sp-3)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={labelStyle}>Computed fee</span>
            <span style={{ ...valueStyle, fontSize: 'var(--fs-lg)' }}>
              {result.amount != null ? `$${result.amount.toLocaleString()}` : '—'}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={labelStyle}>Billing type</span>
            <Badge variant="info">{(result.billing_type ?? 'contingency').replace(/_/g, ' ')}</Badge>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={labelStyle}>Rate source</span>
            <span style={valueStyle}>{SOURCE_LABEL[result.fee_rate_source ?? ''] ?? result.fee_rate_source ?? '—'}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={labelStyle}>Draft invoice</span>
            <span style={{ ...valueStyle, fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-sm)' }}>
              {result.invoice_number ?? '—'}
            </span>
          </div>
        </div>
      )}
    </Modal>
  );
}
